import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { shipmentsApi } from '../../api/endpoints';
import StatusBadge from '../../components/common/StatusBadge';
import { ShipmentDetail } from '../../types';
import Select from '../../components/common/Select';
import { FiSend, FiDownload, FiUpload, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';

const ShipmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    'items' | 'cartons' | 'labels' | 'packing'
  >('items');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docType, setDocType] = useState('PackingSlip');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchShipment = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await shipmentsApi.get(id);
      setShipment(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad pobierania wysylki');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchShipment();
  }, [fetchShipment]);

  const handleSubmitToAmazon = useCallback(async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await shipmentsApi.submit(id);
      toast.success('Wysylka wyslana do Amazon');
      fetchShipment();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad wysylania do Amazon');
    } finally {
      setSubmitting(false);
    }
  }, [id, fetchShipment]);

  const handleDownloadLabel = useCallback(async () => {
    if (!id) return;
    try {
      const response = await shipmentsApi.getLabel(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `label-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad pobierania etykiety');
    }
  }, [id]);

  const handleRequestLabel = useCallback(async () => {
    if (!id) return;
    try {
      await shipmentsApi.requestLabel(id);
      toast.success('Etykieta wygenerowana');
      fetchShipment();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad generowania etykiety');
    }
  }, [id, fetchShipment]);

  const handleUploadPackingDoc = useCallback(async () => {
    if (!id || !fileInputRef.current?.files?.length) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', fileInputRef.current.files[0]);
      formData.append('documentType', docType);
      await shipmentsApi.uploadPackingDoc(id, formData);
      toast.success('Dokument pakowania przeslany');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchShipment();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad przesylania dokumentu');
    } finally {
      setUploadingDoc(false);
    }
  }, [id, docType, fetchShipment]);

  const handleDownloadPackingDoc = useCallback(async (docId: string, fileName: string) => {
    try {
      const response = await shipmentsApi.getPackingDoc(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad pobierania dokumentu');
    }
  }, []);

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">Nie znaleziono wysylki.</div>
        <Link to="/shipments" className="btn btn-secondary">
          Powrot do listy
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/shipments" className="btn btn-link">
            &larr; Wysylki
          </Link>
          <h1>
            Wysylka {shipment.amazonShipmentId || shipment.id.substring(0, 8)}
          </h1>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={handleSubmitToAmazon}
            disabled={submitting}
          >
            <FiSend /> {submitting ? 'Wysylanie...' : 'Wyslij do Amazon'}
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadLabel}>
            <FiDownload /> Pobierz etykiete
          </button>
          <button className="btn btn-secondary" onClick={handleRequestLabel}>
            <FiRefreshCw /> Generuj etykiete
          </button>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-item">
          <span className="detail-label">Numer PO</span>
          <span className="detail-value">
            <Link to={`/purchase-orders/${shipment.purchaseOrderId}`}>
              {shipment.purchaseOrderNumber}
            </Link>
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">ID wysylki Amazon</span>
          <span className="detail-value">{shipment.amazonShipmentId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Status</span>
          <span className="detail-value">
            <StatusBadge type="shipment" value={shipment.status} />
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Data wysylki</span>
          <span className="detail-value">{formatDate(shipment.shipDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Szacowana dostawa</span>
          <span className="detail-value">{formatDate(shipment.estimatedDeliveryDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Faktyczna dostawa</span>
          <span className="detail-value">{formatDate(shipment.actualDeliveryDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Przewoznik</span>
          <span className="detail-value">{shipment.carrierName || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">SCAC</span>
          <span className="detail-value">{shipment.carrierScac || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Numer sledzenia</span>
          <span className="detail-value">{shipment.trackingNumber || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Bill of Lading</span>
          <span className="detail-value">{shipment.billOfLadingNumber || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Adres nadania</span>
          <span className="detail-value">{shipment.shipFromAddress || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Adres dostawy</span>
          <span className="detail-value">{shipment.shipToAddress || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Kartony</span>
          <span className="detail-value">{shipment.totalCartons}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Waga</span>
          <span className="detail-value">
            {shipment.totalWeight} {shipment.weightUnit}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Objetosc</span>
          <span className="detail-value">
            {shipment.totalVolume} {shipment.volumeUnit}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Utworzono</span>
          <span className="detail-value">{formatDate(shipment.createdAt)}</span>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          Pozycje ({shipment.items.length})
        </button>
        <button
          className={`tab ${activeTab === 'cartons' ? 'active' : ''}`}
          onClick={() => setActiveTab('cartons')}
        >
          Kartony ({shipment.cartons.length})
        </button>
        <button
          className={`tab ${activeTab === 'labels' ? 'active' : ''}`}
          onClick={() => setActiveTab('labels')}
        >
          Etykiety ({shipment.labels.length})
        </button>
        <button
          className={`tab ${activeTab === 'packing' ? 'active' : ''}`}
          onClick={() => setActiveTab('packing')}
        >
          Dokumenty pakowania ({shipment.packingDocuments.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'items' && (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>ASIN</th>
                  <th>ID produktu Amazon</th>
                  <th>Wyslana ilosc</th>
                  <th>Jednostka</th>
                </tr>
              </thead>
              <tbody>
                {shipment.items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.itemSequenceNumber}</td>
                    <td>{item.asin || '-'}</td>
                    <td>{item.amazonProductId}</td>
                    <td>{item.shippedQuantity}</td>
                    <td>{item.quantityUnitOfMeasure}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'cartons' && (
          <div className="table-responsive">
            {shipment.cartons.length === 0 ? (
              <p className="text-muted">Brak kartonow.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ID kartonu</th>
                    <th>Wymiary (D x S x W)</th>
                    <th>Waga</th>
                    <th>Tracking</th>
                    <th>SSCC</th>
                    <th>Pozycje</th>
                  </tr>
                </thead>
                <tbody>
                  {shipment.cartons.map((carton) => (
                    <tr key={carton.id}>
                      <td>{carton.cartonSequenceNumber}</td>
                      <td>{carton.cartonId}</td>
                      <td>
                        {carton.length} x {carton.width} x {carton.height}{' '}
                        {carton.dimensionUnit}
                      </td>
                      <td>
                        {carton.weight} {carton.weightUnit}
                      </td>
                      <td>{carton.trackingNumber || '-'}</td>
                      <td>{carton.sscc || '-'}</td>
                      <td>
                        {carton.cartonItems.map((ci) => (
                          <div key={ci.id}>
                            {ci.amazonProductId}: {ci.quantity} szt.
                            {ci.expirationDate &&
                              ` (exp: ${formatDate(ci.expirationDate)})`}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'labels' && (
          <div className="table-responsive">
            {shipment.labels.length === 0 ? (
              <p className="text-muted">Brak etykiet.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Typ</th>
                    <th>Nazwa pliku</th>
                    <th>Typ zawartosci</th>
                    <th>ID etykiety Amazon</th>
                    <th>Utworzono</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {shipment.labels.map((label) => (
                    <tr key={label.id}>
                      <td>{label.labelType}</td>
                      <td>{label.fileName}</td>
                      <td>{label.contentType}</td>
                      <td>{label.amazonLabelId || '-'}</td>
                      <td>{formatDate(label.createdAt)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleDownloadLabel}
                        >
                          <FiDownload /> Pobierz
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'packing' && (
          <div>
            <div className="table-responsive">
              {shipment.packingDocuments.length === 0 ? (
                <p className="text-muted">Brak dokumentow pakowania.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Typ</th>
                      <th>Nazwa pliku</th>
                      <th>Typ zawartosci</th>
                      <th>Uwagi</th>
                      <th>Utworzono</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipment.packingDocuments.map((doc) => (
                      <tr key={doc.id}>
                        <td>{doc.documentType}</td>
                        <td>{doc.fileName}</td>
                        <td>{doc.contentType}</td>
                        <td>{doc.notes || '-'}</td>
                        <td>{formatDate(doc.createdAt)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() =>
                              handleDownloadPackingDoc(doc.id, doc.fileName)
                            }
                          >
                            <FiDownload /> Pobierz
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="card" style={{ marginTop: '1rem' }}>
              <div className="card-header">
                <h3>Przeslij dokument pakowania</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Typ dokumentu</label>
                    <Select
                      options={[
                        { value: 'PackingSlip', label: 'List przewozowy' },
                        { value: 'BillOfLading', label: 'Konosament' },
                        { value: 'CommercialInvoice', label: 'Faktura handlowa' },
                        { value: 'Other', label: 'Inny' },
                      ]}
                      value={docType}
                      onChange={(value) => setDocType(value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Plik</label>
                    <input
                      type="file"
                      className="form-control"
                      ref={fileInputRef}
                    />
                  </div>
                  <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleUploadPackingDoc}
                      disabled={uploadingDoc}
                    >
                      <FiUpload />{' '}
                      {uploadingDoc ? 'Przesylanie...' : 'Przeslij'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipmentDetailPage;
